<template>
  <div>
    <job-menu :job="job"></job-menu>

    <div class="my-10">
      <div class="flex items-center justify-center px-4">
        <h1 class="text-3xl font-bold text-center">
          {{ __('Job #:id', {number: job.id}) }}
          <span v-if="job.external_id" class="rounded px-2 py-1 bg-gray-200 text-lg whitespace-nowrap">(
            <icon name="suitcase" class="w-4 h-4 inline-block fill-current"></icon>
            {{ job.external_id }})
          </span>
        </h1>
        <div class="w-32 ml-2" v-if="job.rating !== null">
          <stars :rating="job.rating"></stars>
        </div>
        <div class="ml-2" v-if="job.permissions.takeover">
          <button @click="takeover()" type="submit" class="flex items-center justify-center flex-shrink-0 px-4 py-2 text-white rounded bg-coral-400 hover:bg-coral-300">
            {{ __("Preluare") }}
          </button>
        </div>
      </div>

      <div class="p-4 mt-10 bg-white shadow-md">
        <div class="flex flex-wrap -mx-2 lg:flex-nowrap">
          <div class="flex flex-col items-center w-full p-4 py-5 m-2 bg-gray-300 rounded-lg lg:w-1/2" v-if="job.car">
            <div class="mt-2 text-xl font-bold text-center">
              <p class="font-normal text-gray-400">
                {{ job.car.car_make.name }} {{ job.car.model }}
              </p>
              <p class="mt-4">{{ job.car.registration_number }}</p>
              <p class="mt-4 font-normal text-gray-400" v-if="false">{{job.car.vin}}</p>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row items-center w-full p-4 py-5 m-2 bg-gray-300 rounded-lg lg:w-1/2 space-x-2 space-y-6 sm:space-y-0" v-if="job.client || job.consultant">
            <div class="w-full sm:w-1/2 flex flex-col items-center" v-if="job.client">
              <div class="text-xl">{{ __("Client") }}</div>
              <div class="mt-2 text-xl font-bold">
                <inertia-link :href="route('clients.edit', {client: job.client.id})" class="text-coral-400 hover:text-gray-500">{{ job.client.name }}</inertia-link>
              </div>
            </div>
            <div class="w-full sm:w-1/2 flex flex-col items-center" v-if="job.consultant">
              <div class="text-xl">{{ __('Consultant') }}</div>
              <div class="mt-2 text-xl font-bold">
                {{ job.consultant.name }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col justify-center px-4 py-5 my-2 bg-gray-300 rounded-lg sm:flex-row">
          <div class="flex flex-col items-center w-full my-2 bg-gray-300 rounded-lg sm:w-1/2">
            <div class="mt-2 text-xl font-bold job-status" :class="job.status">
              {{ job.status_label }}
            </div>
          </div>

          <form @submit.prevent="submitJobStatusForm()" class="w-full max-w-md mx-auto sm:w-1/2">
            <div class="mb-2 text-xl">{{ __("Status") }}</div>
            <div class="flex items-center w-full">
              <div class="relative flex-grow mr-2">
                <select v-model="jobStatusForm.status"
                        class="block w-full px-4 py-3 pr-8 leading-tight text-gray-700 border border-gray-200 rounded appearance-none focus:outline-none focus:border-gray-500">
                  <option v-for="status in jobStatuses" :key="status.value" :value="status.value">
                    {{ status.label }}
                  </option>
                </select>
                <div class="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                  <svg class="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                  </svg>
                </div>
              </div>
              <loading-button :loading="submittingJobStatusForm"
                              :disabled="!job.permissions.changeStatus"
                              type="submit"
                              class="justify-center flex-shrink-0 px-4 py-3 text-xl text-white rounded min-w-40 bg-coral-400 hover:bg-coral-300">
                {{ __("Edit") }}
              </loading-button>
            </div>
          </form>
        </div>
      </div>

      <div v-if="job.media_was_deleted" class="mt-10 p-4 bg-white text-center text-gray-600 italic text-sm border border-gray-300 shadow-md">
        {{ __("Some files associated with this job have been permanently deleted.") }}
      </div>

      <div v-if="showReportCardWarning" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div class="bg-white rounded-lg shadow-2xl max-w-lg w-full p-8">
          <div class="flex flex-col items-center text-center">
            <div class="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-5">
              <svg class="w-8 h-8 text-yellow-600 fill-current" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.13 2.5a1 1 0 0 1 1.74 0l8.65 14.99a1 1 0 0 1-.87 1.51H1.35a1 1 0 0 1-.87-1.51L9.13 2.5zM9 8v4h2V8H9zm0 5v2h2v-2H9z"/>
              </svg>
            </div>
            <h2 class="text-xl font-bold mb-3">
              {{ warningType === 'incomplete' ? __("Report card not complete") : __("Report card PDF not generated") }}
            </h2>
            <p class="text-sm text-gray-600 leading-relaxed">
              <template v-if="warningType === 'incomplete'">
                {{ __("To finalize this job, you must complete the report card. At least one item must have an investigated report, an operation and a technician selected, and the road test user and supervisor must be set.") }}
              </template>
              <template v-else>
                {{ __("The report card is complete, but the PDF has not been generated. Open the report card and click Generate PDF before finalizing this job.") }}
              </template>
            </p>
          </div>
          <div class="mt-8 flex justify-center gap-3">
            <button type="button" class="px-6 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100" @click="cancelStatusChange">
              {{ __("Cancel") }}
            </button>
            <button type="button" class="px-6 py-2 rounded bg-coral-400 text-white hover:bg-coral-500" @click="goToReportCard">
              {{ __("Go to report card") }}
            </button>
          </div>
        </div>
      </div>

      <job-medialibrary :job="job" :editable="true" @uploading="uploadingMedia = true" @finished="uploadingMedia = false"></job-medialibrary>

      <div class="px-5 py-10 mt-10 bg-white shadow-md">
        <chat :job="job" :conversation="conversation" :messages="messages"></chat>
      </div>

      <div class="px-5 py-10 mt-10 bg-white shadow-md" v-if="job.permissions.showOperations">
        <operations :job="job" :operations="operations"></operations>
      </div>

      <div class="p-10 mt-10 bg-white shadow-md" v-if="job.permissions.edit">
        <h1 class="text-3xl font-bold text-center">{{ __('Job #:id', {'number': job.id})}}</h1>
        <div v-if="!job.infoComplete && quickJobRegistrationNumber" class="w-full max-w-2xl p-4 mx-auto mt-4 bg-blue-100 rounded">
          {{ __('Job created for nr: :registration_number', {'registration_number': quickJobRegistrationNumber }) }}
        </div>

        <edit-job-form :cars="cars" :clients="clients" :technicians="technicians" :consultants="consultants" :job="job" class="mt-10"></edit-job-form>
      </div>

      <div class="px-5 py-10 mt-10 bg-white shadow-md" v-if="authUser().isTechnician() || authUser().isConsultant()">
        <h2 class="text-3xl text-center">{{ __("Job description") }}</h2>
        <div class="max-w-4xl mx-auto mt-12 text-xl leading-normal">
          {{ job.description }}
        </div>
      </div>

      <div class="px-5 py-10 mt-10 bg-white shadow-md" v-if="!authUser().isTechnician() || authUser().isConsultant()">
        <h2 class="text-3xl text-center">{{ __("Observations") }}</h2>
        <div class="max-w-4xl mx-auto mt-12 text-xl leading-normal">
          {{ job.observations || '-'}}
        </div>
      </div>

      <div class="px-5 py-10 my-10 bg-white shadow-md" v-if="job.permissions.editObservations">
        <h2 class="text-3xl text-center">{{ __("Observations") }}</h2>

        <div class="max-w-4xl mx-auto mt-10 text-xl leading-normal">
          <form @submit.prevent="submitObservationsForm()">
            <div class="flex flex-wrap -mx-4">
              <textarea-input v-model="observationsForm.observations" class="w-full pb-8" :disabled="jobComplete" rows="10"/>
            </div>
            <div class="flex items-center justify-end ">
              <loading-button :loading="submittingObservationsForm" v-if="!jobComplete" class="-mx-4 btn-secondary" type="submit">
                {{ __("Save") }}
              </loading-button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Layout from '@/Shared/Layout';
  import Icon from '@/Shared/Icon';
  import TextareaInput from '@/Shared/TextareaInput';
  import LoadingButton from '@/Shared/LoadingButton';
  import Stars from '@/Shared/Stars';
  import EditJobForm from './EditJobForm';
  import JobMedialibrary from './JobMedialibrary';
  import Operations from './Operations';
  import JobMenu from './JobMenu';
  import Chat from "../../Shared/Chat";

  export default {
    layout: Layout,
    metaInfo() {
      return {
        title: this.__("Jobs"),
      }
    },
    props: {
      job: Object,
      view: Object,
      clients: Object,
      technicians: Object,
      consultants: Object,
      cars: Array,
      operations: Array,
      messages: Object,
      conversation: Object
    },
    components: {
      Layout,
      LoadingButton,
      TextareaInput,
      Icon,
      EditJobForm,
      JobMedialibrary,
      Operations,
      JobMenu,
      Chat,
      Stars
    },
    data() {
      return {
        jobStatuses: [
          {
            value: 'cancelled',
            label: this.__('jobStatus.cancelled'),
          },
          {
            value: 'pending',
            label: this.__('jobStatus.pending'),
          },
          {
            value: 'completed',
            label: this.__('jobStatus.completed'),
          },
        ],
        uploadingMedia: false,
        submittingObservationsForm: false,
        submittingJobStatusForm: false,
        observationsForm: {
          observations: this.job.observations
        },
        jobStatusForm: {
          status: this.job.status
        },
        showReportCardWarning: false,
        warningType: 'incomplete',
      }
    },
    mounted() {
      window.Echo.channel('App.Models.Job.' + this.job.id)
        .listen('JobFilesAddedEvent', (data) => this.handleJobWasUpdated(data))
        .listen('JobUpdatedEvent', (data) => this.handleJobWasUpdated(data));
    },
    destroyed() {
      window.Echo.channel('App.Models.Job.' + this.job.id)
        .stopListening('JobFilesAddedEvent')
        .stopListening('JobUpdatedEvent');
    },
    methods: {
      handleJobWasUpdated(data) {
        if (!this.uploadingMedia && this.$page.props.auth.user.id !== data.user_id) {
          this.$inertia.reload({preserveScroll: true, preserveState: true});
        }
      },
      submitObservationsForm() {
        this.submittingObservationsForm = true;
        this.$inertia.put(this.route('jobObservations.update', {job: this.job.id}), this.observationsForm, {
          preserveScroll: true
        }).finally(() => {
          this.submittingObservationsForm = false;
        })
      },
      submitJobStatusForm() {
        if (this.jobStatusForm.status === 'completed' && this.job.reportCardEnabled && this.job.reportCardMandatory) {
          if (!this.job.reportCardComplete) {
            this.warningType = 'incomplete';
            this.showReportCardWarning = true;
            return;
          }
          if (!this.job.reportCardFinalized) {
            this.warningType = 'no_pdf';
            this.showReportCardWarning = true;
            return;
          }
        }
        this.submittingJobStatusForm = true;
        this.$inertia.put(this.route('jobStatus.update', {job: this.job.id}), this.jobStatusForm, {
          preserveScroll: false,
          preserveState: false,
          onFinish: () => { this.submittingJobStatusForm = false; },
        });
      },
      cancelStatusChange() {
        this.showReportCardWarning = false;
        this.jobStatusForm.status = this.job.status;
      },
      goToReportCard() {
        this.showReportCardWarning = false;
        this.$inertia.visit(this.route('reportCards.show', {job: this.job.id}));
      },
      takeover() {
        if (confirm(this.__("Are you sure you want to takeover this job?"))) {
          this.$inertia.put(this.route('job.takeover', {job: this.job.id}), {}, {preserveScroll: false, preserveState: false});
        }
      }
    },
    computed: {
      jobComplete() {
        return this.job.status === 'completed';
      },
      quickJobRegistrationNumber() {
        if (!this.job.hasOwnProperty('meta') || !this.job.meta || !this.job.meta.hasOwnProperty('registration_number')) {
          return null;
        }

        return this.job.meta.registration_number;
      }
    }
  }
</script>
